import { createSlice } from '@reduxjs/toolkit';

const initialState = {
    // Configs organized by tableName: { [tableName]: { columns: [], loading: false, error: null, lastFetched: null } }
    configs: {},
    globalLoading: false,
    globalError: null,
};

const tableColumnConfigSlice = createSlice({
    name: 'tableColumnConfig',
    initialState,
    reducers: {
        // Request action - triggered when fetching starts
        fetchTableColumnsRequest: (state, action) => {
            const { tableName } = action.payload;
            if (!state.configs[tableName]) {
                state.configs[tableName] = { columns: [], loading: false, error: null, lastFetched: null };
            }
            state.configs[tableName].loading = true;
            state.configs[tableName].error = null;
        },

        // Success action - store fetched configs
        fetchTableColumnsSuccess: (state, action) => {
            const { tableName, columns } = action.payload;
            state.configs[tableName] = {
                columns: columns,
                loading: false,
                error: null,
                lastFetched: Date.now(),
            };
        },

        // Failure action
        fetchTableColumnsFailure: (state, action) => {
            const { tableName, error } = action.payload;
            if (state.configs[tableName]) {
                state.configs[tableName].loading = false;
                state.configs[tableName].error = error;
            }
        },

        // Clear configs (for logout/domain switch)
        clearTableColumnConfigs: (state) => {
            state.configs = {};
            state.globalLoading = false;
            state.globalError = null;
        },

        // Update single column visibility (for future UI toggle feature)
        updateColumnVisibility: (state, action) => {
            const { tableName, columnKey, isVisible } = action.payload;
            if (state.configs[tableName]?.columns) {
                const column = state.configs[tableName].columns.find(c => c.key === columnKey);
                if (column) {
                    column.isVisible = isVisible;
                }
            }
        },
    },
});

export const {
    fetchTableColumnsRequest,
    fetchTableColumnsSuccess,
    fetchTableColumnsFailure,
    clearTableColumnConfigs,
    updateColumnVisibility,
} = tableColumnConfigSlice.actions;

export default tableColumnConfigSlice.reducer;

// Selectors
const EMPTY_ARRAY = [];

export const selectTableColumns = (tableName) => (state) => {
    const columns = state.tableColumnConfig?.configs?.[tableName]?.columns;
    // Return stable EMPTY_ARRAY if columns is undefined OR empty
    // This prevents reference changes when reducer initializes with []
    if (!columns || columns.length === 0) {
        return EMPTY_ARRAY;
    }
    return columns;
};

export const selectTableColumnsLoading = (tableName) => (state) =>
    state.tableColumnConfig?.configs?.[tableName]?.loading ?? false;

export const selectTableColumnsError = (tableName) => (state) =>
    state.tableColumnConfig?.configs?.[tableName]?.error;

export const selectTableColumnsLastFetched = (tableName) => (state) =>
    state.tableColumnConfig?.configs?.[tableName]?.lastFetched;

export const selectAllTableConfigs = (state) =>
    state.tableColumnConfig?.configs || {};
